import React, { useState } from "react";
import axios from "axios";
import { ImagePlus, Copy, FileText, Film, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/store";
import { fmtDate, fmtSize } from "./adminShared";
import { Button } from "../ui/button";

export function MediaTab({ auth, media, setMedia }) {
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: too large (max 8 MB)`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await axios.post(`${API}/admin/media/upload`, fd, auth);
        setMedia((m) => [res.data, ...m]);
        toast.success(`${f.name} uploaded`);
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
  };

  const copyMediaUrl = (id) => {
    navigator.clipboard.writeText(`${API}/media/${id}`);
    toast.success("URL copied to clipboard");
  };

  const deleteMedia = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(`${API}/admin/media/${id}`, auth);
      setMedia((l) => l.filter((m) => m.id !== id));
      toast.success("File deleted");
    } catch { toast.error("Failed to delete file"); }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white border border-[#eee6d6] p-6">
        <h3 className="font-heading text-xl text-[#171310]">Media Library</h3>
        <p className="text-sm text-[#8f8571] mt-1">
          Upload store photos, banners and documents (max 8 MB each — JPG, PNG, WebP, GIF, SVG, MP4, PDF). Copy a file's URL to use it anywhere.
        </p>
        <label className={`mt-5 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#d8cdb5] py-10 cursor-pointer hover:border-[#a8842c] hover:bg-[#faf7f0] transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="media-upload-dropzone">
          <ImagePlus size={26} strokeWidth={1.3} className="text-[#a8842c]" />
          <span className="text-sm text-[#5c5240]">{uploading ? "Uploading..." : "Click to choose files (or select multiple)"}</span>
          <input type="file" multiple accept="image/*,video/mp4,application/pdf" className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="media-upload-input" />
        </label>
      </div>

      {media.length === 0 ? (
        <p className="text-[#8f8571] text-sm py-6 text-center">No files uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="media-grid">
          {media.map((m) => (
            <div key={m.id} className="bg-white border border-[#eee6d6] overflow-hidden" data-testid="media-item">
              <div className="aspect-square bg-[#faf7f0] flex items-center justify-center overflow-hidden">
                {m.contentType?.startsWith("image/") ? (
                  <img src={`${API}/media/${m.id}`} alt={m.filename} loading="lazy" className="w-full h-full object-cover" />
                ) : m.contentType === "video/mp4" ? (
                  <Film size={32} strokeWidth={1.2} className="text-[#a8842c]" />
                ) : (
                  <FileText size={32} strokeWidth={1.2} className="text-[#a8842c]" />
                )}
              </div>
              <div className="p-3">
                <p className="text-xs text-[#2b261e] truncate" title={m.filename}>{m.filename}</p>
                <p className="text-[10px] text-[#9c9482] mt-0.5">{fmtSize(m.size)} · {fmtDate(m.createdAt)}</p>
                <div className="flex gap-2 mt-2.5">
                  <Button variant="outline" size="sm" onClick={() => copyMediaUrl(m.id)} className="flex-1 rounded-none border-[#d8cdb5] h-8 text-[11px] uppercase tracking-wider" data-testid="media-copy-url-btn">
                    <Copy size={12} className="mr-1.5" /> Copy URL
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => deleteMedia(m.id)} className="rounded-none border-[#d8cdb5] h-8 w-8 text-[#8c2f1b] hover:bg-[#8c2f1b] hover:text-white shrink-0" aria-label="Delete file" data-testid="media-delete-btn">
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
